/**
 * The planning proposal attached to Hugin's chat (#163, owner 18.10: one Hugin chat). «Juster med Hugin»
 * in Vaktplan's planning panel attaches the proposal it shows; the floating chat sends it with every
 * message, draws «Forslaget vi jobber med» and the week line, and hands Hugin's edits back to the panel,
 * which makes them into the next version. Closing the proposal detaches it.
 */
import type { BasisRef } from '@/pages/vaktplan/planning/api';
import { create } from 'zustand';

export interface Attached {
  /** what the server checks and Hugin reads: week, team, candidate, revision, edits, basis */
  context: Record<string, unknown>;
  /** «uke 43 · Fjordlys Kafé (demo)» */
  line: string;
  week: number;
  company: string;
  /** «vaktmalen Normaluke», «tidligere uker» or null */
  basis: string | null;
  /** «Forslag: Lavest kostnad» */
  title: string;
  version: number;
  /** «23 993 kr · 56 timer» */
  facts: string;
}

interface State {
  attached: Attached | null;
  /** the panel's own: makes Hugin's edits the next version, answers its number, or null when refused */
  onEdits: ((edits: unknown[]) => Promise<number | null>) | null;
  /** the panel's own «Lagre som utkast», for the chat's save button (owner 18.10); true when saved */
  onSave: (() => Promise<boolean>) | null;
  attach: (a: Attached, onEdits: State['onEdits']) => void;
  update: (a: Partial<Attached>) => void;
  detach: () => void;
}

export const useAttached = create<State>((set) => ({
  attached: null,
  onEdits: null,
  onSave: null,
  attach: (attached, onEdits) => set({ attached, onEdits }),
  update: (a) => set((s) => (s.attached ? { attached: { ...s.attached, ...a } } : {})),
  detach: () => set({ attached: null, onEdits: null, onSave: null }),
}));

/** Open the floating chat, and optionally send a message in it at once (the comparison's «Prøv denne føringen»). */
let toSend: string | null = null;
/** The message waiting to be sent once the conversation is on screen, taken once. */
export const takeSend = () => {
  const t = toSend;
  toSend = null;
  return t;
};
export function openHugin(send?: string) {
  if (send) toSend = send;
  // on Vaktplan the chat lives in the side panel's Hugin tab (PocChatPanel redirects); elsewhere it floats
  window.dispatchEvent(new Event('timefly:ai-open'));
  if (send) window.dispatchEvent(new Event('timefly:ai-send'));
}

/** One tool answer as the object it carries, or null. */
function answerOf(output: unknown): Record<string, unknown> | null {
  const text =
    typeof output === 'string'
      ? output
      : ((output as { content?: { text?: string }[] })?.content?.map((c) => c.text ?? '').join('') ?? '');
  try {
    const o = JSON.parse(text) as unknown;
    return o && typeof o === 'object' ? (o as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Hugin's edits in one tool answer (adjust_plan_proposal), if it carries any. */
export function editsIn(output: unknown): unknown[] | null {
  const e = answerOf(output)?.planning_edits;
  return Array.isArray(e) ? e : null;
}

/** A week plan from the chat (plan_week, owner 18.10: one engine): Planlegg opens on this week with this basis. */
export interface ProposalAsk {
  week_start: string;
  basis: BasisRef | null;
  /** the limits the manager agreed to lift in the chat (owner 18.10) */
  lift?: ('contract' | 'availability')[];
}
/** The week plan in one tool answer (plan_week), if it carries one. */
export function proposalIn(output: unknown): ProposalAsk | null {
  const p = answerOf(output)?.planning_proposal as ProposalAsk | undefined;
  return p && typeof p.week_start === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(p.week_start)
    ? {
        week_start: p.week_start,
        basis: p.basis ?? null,
        ...(Array.isArray(p.lift) && p.lift.length ? { lift: p.lift } : {}),
      }
    : null;
}
/** The week plan the chat asked for, until Vaktplan's planning panel opens it on that week. */
export const useProposalAsk = create<{
  asked: ProposalAsk | null;
  ask: (p: ProposalAsk) => void;
  clear: () => void;
}>((set) => ({
  asked: null,
  ask: (asked) => set({ asked }),
  clear: () => set({ asked: null }),
}));

/**
 * Hugin docked in Vaktplan's side panel (owner 18.10: "hugin should be in sidebar on vaktplan always").
 * There is still one conversation: the floating chat's own (PocChatPanel), drawn into the panel's Hugin
 * tab when Vaktplan offers a place for it. `dockable` says Vaktplan is on screen on a desktop; `target`
 * is the Hugin tab's element while that tab shows. On a phone and on every other page nothing docks.
 */
export const useDock = create<{
  dockable: boolean;
  target: HTMLElement | null;
  /** «uke 43 · Fjordlys Kafé (demo)»: what the docked chat names as its context with no proposal attached */
  line: string;
  /** what the week on screen holds as saved, sent with each message so Hugin knows the state (owner 18.10) */
  week: WeekState | null;
  set: (
    s: Partial<{ dockable: boolean; target: HTMLElement | null; line: string; week: WeekState | null }>,
  ) => void;
}>((set) => ({ dockable: false, target: null, line: '', week: null, set: (s) => set(s) }));

/** The saved week on screen: its Monday and counts of what is published, drafted and open. */
export interface WeekState {
  week_start: string;
  published: number;
  drafts: number;
  open: number;
}

/** The save offer in one tool answer (offer_plan_save): week, shifts and how many stay open, or null. */
export function saveOfferIn(output: unknown): { week: number; shifts: number; open: number } | null {
  const text =
    typeof output === 'string'
      ? output
      : ((output as { content?: { text?: string }[] })?.content?.map((c) => c.text ?? '').join('') ?? '');
  try {
    const o = JSON.parse(text) as { planning_save?: { week: number; shifts: number; open: number } };
    return o.planning_save ?? null;
  } catch {
    return null;
  }
}
